import React, { useEffect, useRef, useState } from 'react';
import { DatePicker, Select, Input, Button, Row, Col } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { getAuditLogs } from '../../../Apis/God';
import TanStackAuditTable from './TanStackAuditTable';
import { columns } from './tableConfig';
import PageTitle from '../../../Utils/PageTitle';
import { TitleRow, ContainerRow } from '../styledComponents';
import { RESULTS_PER_PAGE } from '../../../Constants/StringConstants';
import CustomTypography from '../../CustomComponents/CustomTypography';

const { RangePicker } = DatePicker;

const SOURCE_OPTIONS = [
  'MOBILE',
  'MOBILE_WEBAPP',
  'WEB',
  'UNKNOWN'
];

const AuditLogs = () => {
  const [theme] = useSelector(state => [state.themeReducer]);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);

  const [dateRange, setDateRange] = useState(null);
  const [source, setSource] = useState(undefined);
  const [userId, setUserId] = useState('');
  const isFirstRender = useRef(true);
  const skipNextFetch = useRef(false);

  const disabledDate = current => current && current > moment().endOf('day');

  const fetchLogs = async (page = 0, reset = false, userIdVal = userId) => {
    try {
      setLoading(true);
      const params = {
        page,
        size: RESULTS_PER_PAGE
      };
      if (!reset) {
        if (dateRange && dateRange[0]) {
          params.fromDate = dateRange[0].startOf('day').valueOf();
        }
        if (dateRange && dateRange[1]) {
          params.toDate = dateRange[1].endOf('day').valueOf();
        }
        if (source) {
          params.source = source;
        }
        if (userIdVal.trim()) {
          params.userId = userIdVal.trim();
        }
      }
      const res = await getAuditLogs(params);
      const list = Array.isArray(res?.data?.result) ? res.data.result : [];
      setLogs(list);
      setTotal(res?.data?.size || list.length);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const pageNoChangeHandler = pageNo => {
    setCurrentPage(pageNo);
    fetchLogs(pageNo);
  };

  const handleSearch = () => {
    setCurrentPage(0);
    fetchLogs(0);
  };

  const handleReset = () => {
    setDateRange(null);
    setSource(undefined);
    setUserId('');
    setCurrentPage(0);
    fetchLogs(0, true);
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isFirstRender.current) return;
    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      return;
    }
    setCurrentPage(0);
    fetchLogs(0);
    // eslint-disable-next-line
  }, [dateRange]);

  useEffect(() => {
    if (isFirstRender.current) return;
    setCurrentPage(0);
    fetchLogs(0);
    // eslint-disable-next-line
  }, [source]);

  useEffect(() => {
    isFirstRender.current = false;
  }, []);

  return (
    <>
      <PageTitle title='Audit Logs' />
      <ContainerRow gutter={[0, 40]}>
        <TitleRow justify='space-between'>
          <CustomTypography color={theme['@text-color']}>
            Audit Logs
          </CustomTypography>
        </TitleRow>

        <Row gutter={[12, 12]} justify='end' style={{ width: '100%', marginBottom: 16 }}>
          <Col>
            <RangePicker
              value={dateRange}
              onChange={val => setDateRange(val)}
              onOpenChange={open => {
                if (open && dateRange) {
                  skipNextFetch.current = true;
                  setDateRange(null);
                }
              }}
              disabledDate={disabledDate}
              showTime={false}
              format='YYYY-MM-DD'
            />
          </Col>
          <Col>
            <Select
              placeholder='Source'
              allowClear
              style={{ width: 180 }}
              value={source}
              onChange={val => setSource(val)}
            >
              {SOURCE_OPTIONS.map(opt => (
                <Select.Option key={opt} value={opt}>
                  {opt}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col>
            <Input
              placeholder='User ID'
              allowClear
              style={{ width: 250 }}
              value={userId}
              onChange={e => {
                setUserId(e.target.value);
                if (!e.target.value) {
                  setCurrentPage(0);
                  fetchLogs(0, false, '');
                }
              }}
              onPressEnter={handleSearch}
            />
          </Col>
          <Col>
            <Button
              type='primary'
              icon={<SearchOutlined />}
              onClick={handleSearch}
            >
              Search
            </Button>
          </Col>
          <Col>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              Reset
            </Button>
          </Col>
        </Row>

        <TanStackAuditTable
          data={logs}
          columns={columns}
          loading={loading}
          total={total}
          pageNo={currentPage}
          pageSize={RESULTS_PER_PAGE}
          pageNoChangeHandler={pageNoChangeHandler}
        />
      </ContainerRow>
    </>
  );
};

export default AuditLogs;
